/// <reference types="@types/google.maps" />
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { DEFAULT_GOOGLE_MAP_OPTIONS, MapMarkerType, MapRequest, RmaMapMarkerDetail } from '@rma/generic/maps/domain';
import { MapMarkerService } from '@rma/generic/maps/util';
import { MapsStore } from '@rma/public/maps/api';
import { MapFilters } from '@rma/public/maps/domain';
import { MarkerTypeButtonGroupComponent } from '@rma/public/maps/private/ui-marker-type-toggle';
import { GoogleMapComponent } from '@rma/public/maps/public/ui-google-map';

@Component({
  selector: 'rma-inline-map',
  imports: [AsyncPipe, GoogleMapComponent, TranslocoModule, MarkerTypeButtonGroupComponent],
  templateUrl: './inline-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MapsStore, MapMarkerService, provideTranslocoScope('maps')],
})
export class InlineMapComponent implements OnChanges {
  @Input({ required: true })
  mapRequest!: MapRequest;

  @Input()
  mapTypes: Readonly<MapMarkerType[]> | null = ['Review', 'CompletedListing', 'ActiveListing'];

  @Input()
  fullscreenRoute: string | null = null;

  protected readonly store = inject(MapsStore);

  protected options: google.maps.MapOptions = {
    ...DEFAULT_GOOGLE_MAP_OPTIONS,
    disableDefaultUI: true,
  };

  ngOnChanges(changes: SimpleChanges): void {
    const { mapRequest } = changes;

    if (mapRequest && this.mapRequest) {
      this.store.setMapRequest(this.mapRequest);
      // required because other implmentations use the filters component that needs to emit before continuing
      this.store.setFilters(this.getFilters(this.mapRequest.markerType));
    }
  }

  onBoundsChanged(bounds: google.maps.LatLngLiteral[] | null) {
    if (bounds) {
      const request: Partial<MapRequest> = {
        locationId: undefined,
        locationType: undefined,
        shape: {
          shapeType: 'Envelope',
          shapeCoordinates: bounds.map((b) => ({ lat: b.lat, lon: b.lng })),
        },
      };

      this.store.updateMapRequest(request);
    }
  }

  onMarkerSelected(mapMarker: RmaMapMarkerDetail) {
    this.store.setActiveMarkerEffect(mapMarker);
  }

  onMapTypeSelected(mapType: MapMarkerType) {
    this.store.updateMapRequest({ markerType: mapType });
    this.store.setFilters(this.getFilters(mapType));
  }

  private getFilters(mapType: MapMarkerType): MapFilters {
    if (mapType === 'Review') {
      return {
        minStarRating: 1,
      };
    }

    return {
      statuses: mapType === 'ActiveListing' ? 'Active' : 'Sold',
    };
  }
}
